import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'

import {
  AccountingCharts,
  ChartStatus,
  DateRanges,
  LiquidCashReport,
  QueryAccountingReportInput,
} from '../../../../__generated__/api-types-and-hooks'
import { AppAccountingStatValue } from 'components/AppDataReport/AppData/AppAccountingStatValue'
import OwnerApi from 'api/ownerData'
import { OperationName } from 'types'
import withChartWrapper, { WithChartWrapperProps } from 'components/AppDataReport/AppData/ChartHOC'
import { useFetchChartData } from 'hooks/useFetchChartData'
import { RootState } from 'App'
import { ROLES } from 'config'
import { getRoleFromPath } from 'utils/helper'
import { ReduxAction } from 'store/types'
import Loader from 'components/Loaders'

export interface IAppLiquidCashChartProps {
  chartData: { data?: LiquidCashReport[]; status: ChartStatus }
  isLoading?: boolean
  getDataAction: ReduxAction<QueryAccountingReportInput>
  period?: DateRanges
}

type AppLiquidCashData = { data?: LiquidCashReport[]; status: ChartStatus }

const onFetchDataMethod = async (
  params: QueryAccountingReportInput
): Promise<AppLiquidCashData | undefined> => {
  const data = await OwnerApi.getOwnerAccountingData<LiquidCashReport[]>(params)
  return data?.getAccountingCharts
}

const ACCOUNTING_CHARTS_QUERY_KEY: OperationName = 'getAccountingCharts'

interface IAppLiquidCashProps
  extends WithChartWrapperProps<QueryAccountingReportInput, AppLiquidCashData> {
  period: DateRanges
}

export const AppLiquidCashChart = withChartWrapper(
  onFetchDataMethod,
  ({ onFetchData, chartData, period }: IAppLiquidCashProps) => {
    const { mutate: fetchData, isLoading } = useFetchChartData(
      onFetchData,
      ACCOUNTING_CHARTS_QUERY_KEY
    )

    const userId =
      getRoleFromPath() === ROLES.BUSINESS_OWNER
        ? useSelector((state: RootState) => state.user.user.id)
        : useSelector((state: RootState) => state.clients.client.profile.id)

    useEffect(() => {
      fetchData({
        chartType: AccountingCharts.LiquidCash,
        dateRange: period,
        userId,
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period])

    return (
      <div className="flex flex-col font-semibold leading-7 text-lg font-primary p-10">
        <p>LIQUID CASH</p>
        {isLoading && <Loader loader="TableLoader" />}
        {chartData?.data?.length ? (
          <AppAccountingStatValue
            isLoading={isLoading}
            chartValue={chartData?.data[0].liquidCash ?? 0}
            chartGrowthValue={chartData?.data[0].growth ?? 0}
            dateRange={chartData?.data[0].dateRange ?? DateRanges.ThisYearToDate}
          />
        ) : (
          <></>
        )}
      </div>
    )
  }
)
